import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Checkbox } from '@/components/ui/checkbox';
import type { TrustedOperator, CreateTrustedOperatorBody } from '@/services/api.client';

const DAYS = [
  { value: 'MON', label: 'Mon' },
  { value: 'TUE', label: 'Tue' },
  { value: 'WED', label: 'Wed' },
  { value: 'THU', label: 'Thu' },
  { value: 'FRI', label: 'Fri' },
  { value: 'SAT', label: 'Sat' },
  { value: 'SUN', label: 'Sun' },
] as const;

interface AllowedWindow {
  days: string[];
  from: string;
  until: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  operator: TrustedOperator | null;
  onSubmit: (body: CreateTrustedOperatorBody) => void;
  isSubmitting: boolean;
}

export function TrustedOperatorDrawer({
  open,
  onOpenChange,
  operator,
  onSubmit,
  isSubmitting,
}: Props) {
  const { t } = useTranslation();
  const isEdit = !!operator;

  const [name, setName] = useState('');
  const [operatorType, setOperatorType] = useState<string>('school_bus');
  const [notes, setNotes] = useState('');
  const [notifyAll, setNotifyAll] = useState(false);
  const [maxDaily, setMaxDaily] = useState('');
  const [windows, setWindows] = useState<AllowedWindow[]>([
    { days: ['MON', 'TUE', 'WED', 'THU', 'FRI'], from: '07:00', until: '08:30' },
  ]);

  useEffect(() => {
    if (operator) {
      setName(operator.name);
      setOperatorType(operator.operator_type);
      setNotes(operator.notes ?? '');
      setNotifyAll(operator.notify_all_on_entry);
      setMaxDaily(operator.max_daily_entries?.toString() ?? '');
      setWindows(
        operator.allowed_windows.length > 0
          ? operator.allowed_windows
          : [{ days: ['MON', 'TUE', 'WED', 'THU', 'FRI'], from: '07:00', until: '08:30' }],
      );
    } else {
      setName('');
      setOperatorType('school_bus');
      setNotes('');
      setNotifyAll(false);
      setMaxDaily('');
      setWindows([{ days: ['MON', 'TUE', 'WED', 'THU', 'FRI'], from: '07:00', until: '08:30' }]);
    }
  }, [operator, open]);

  const handleDayToggle = (windowIndex: number, day: string) => {
    setWindows((prev) =>
      prev.map((w, i) => {
        if (i !== windowIndex) return w;
        const days = w.days.includes(day) ? w.days.filter((d) => d !== day) : [...w.days, day];
        return { ...w, days };
      }),
    );
  };

  const handleWindowTimeChange = (windowIndex: number, field: 'from' | 'until', value: string) => {
    setWindows((prev) => prev.map((w, i) => (i === windowIndex ? { ...w, [field]: value } : w)));
  };

  const addWindow = () => {
    setWindows((prev) => [
      ...prev,
      { days: ['MON', 'TUE', 'WED', 'THU', 'FRI'], from: '08:00', until: '17:00' },
    ]);
  };

  const removeWindow = (index: number) => {
    if (windows.length <= 1) return;
    setWindows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const body: CreateTrustedOperatorBody = {
      name: name.trim(),
      operatorType: operatorType as CreateTrustedOperatorBody['operatorType'],
      notifyAllOnEntry: notifyAll,
      allowedWindows: windows.filter((w) => w.days.length > 0),
    };
    if (notes.trim()) body.notes = notes.trim();
    if (maxDaily) body.maxDailyEntries = parseInt(maxDaily, 10);
    onSubmit(body);
  };

  const isValid = name.trim().length > 0 && windows.some((w) => w.days.length > 0);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>
            {isEdit
              ? t('trustedOperators.editTitle', 'Edit Operator')
              : t('trustedOperators.createTitle', 'Add Trusted Operator')}
          </SheetTitle>
          <SheetDescription>
            {isEdit
              ? t('trustedOperators.editDescription', 'Update operator details and schedule.')
              : t(
                  'trustedOperators.createDescription',
                  'Register a pre-approved operator for fast gate entry.',
                )}
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-6 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="op-name">{t('trustedOperators.nameLabel', 'Operator Name')}</Label>
            <Input
              id="op-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t(
                'trustedOperators.namePlaceholder',
                'e.g. Springfield Primary — School Bus',
              )}
              maxLength={100}
              required
            />
          </div>

          {/* Type */}
          <div className="space-y-2">
            <Label>{t('trustedOperators.typeLabel', 'Operator Type')}</Label>
            <Select value={operatorType} onValueChange={setOperatorType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="school_bus">
                  {t('trustedOperators.typeSchoolBus', 'School Bus')}
                </SelectItem>
                <SelectItem value="shuttle">
                  {t('trustedOperators.typeShuttle', 'Shuttle')}
                </SelectItem>
                <SelectItem value="service_provider">
                  {t('trustedOperators.typeServiceProvider', 'Service Provider')}
                </SelectItem>
                <SelectItem value="other">{t('trustedOperators.typeOther', 'Other')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="op-notes">{t('trustedOperators.notesLabel', 'Notes for guard')}</Label>
            <Textarea
              id="op-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t(
                'trustedOperators.notesPlaceholder',
                'e.g. Blue bus, arrives at main gate',
              )}
              maxLength={255}
              rows={2}
            />
          </div>

          {/* Notify all */}
          <div className="flex items-center justify-between">
            <Label htmlFor="op-notify">
              {t('trustedOperators.notifyAllLabel', 'Notify all residents on entry')}
            </Label>
            <Switch id="op-notify" checked={notifyAll} onCheckedChange={setNotifyAll} />
          </div>

          {/* Max daily entries */}
          <div className="space-y-2">
            <Label htmlFor="op-max-daily">
              {t('trustedOperators.maxDailyLabel', 'Max daily entries (blank = unlimited)')}
            </Label>
            <Input
              id="op-max-daily"
              type="number"
              min={1}
              value={maxDaily}
              onChange={(e) => setMaxDaily(e.target.value)}
              placeholder="∞"
            />
          </div>

          {/* Allowed Windows */}
          <div className="space-y-3">
            <Label>{t('trustedOperators.windowsLabel', 'Allowed Entry Windows')}</Label>
            {windows.map((window, idx) => (
              <div key={idx} className="rounded-lg border border-border p-3 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-[var(--text-secondary)]">
                    {t('trustedOperators.windowNumber', 'Window {{n}}', { n: idx + 1 })}
                  </span>
                  {windows.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeWindow(idx)}
                    >
                      <X className="size-3" />
                    </Button>
                  )}
                </div>
                {/* Days */}
                <div className="flex flex-wrap gap-2">
                  {DAYS.map((day) => (
                    <label key={day.value} className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox
                        checked={window.days.includes(day.value)}
                        onCheckedChange={() => handleDayToggle(idx, day.value)}
                      />
                      <span className="text-xs">{day.label}</span>
                    </label>
                  ))}
                </div>
                {/* Times */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('trustedOperators.from', 'From')}</Label>
                    <Input
                      type="time"
                      value={window.from}
                      onChange={(e) => handleWindowTimeChange(idx, 'from', e.target.value)}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('trustedOperators.until', 'Until')}</Label>
                    <Input
                      type="time"
                      value={window.until}
                      onChange={(e) => handleWindowTimeChange(idx, 'until', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addWindow}>
              <Plus className="size-3 mr-1" />
              {t('trustedOperators.addWindow', 'Add another window')}
            </Button>
          </div>

          <SheetFooter>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting
                ? t('common.saving', 'Saving...')
                : isEdit
                  ? t('common.save', 'Save')
                  : t('trustedOperators.create', 'Create Operator')}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
